'use client';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from 'Architecture/api';
import Output from '../Config/CustomElement/Editor/Output';
import { FaFacebookSquare, FaInstagram, FaLinkedin, FaWhatsapp, FaBookmark } from 'react-icons/fa';
import { FaSquareXTwitter } from 'react-icons/fa6';
import { LuShare2 } from 'react-icons/lu';

const defaultArticle = {
    title: '',
    subtitle: '',
    description: '',
    content: '',
    category: '',
    articleSeries: '',
    articleLength: 0,
    articleSource: '',
    seoTitle: '',
    metaDescription: '',
    metaTags: '',
    slug: '',
    author: '',
    publishDate: new Date().toISOString().split('T')[0],
    status: 2,
    approvedBy: '',
    approvedAt: null,
    rejectionReason: '',
    excerpt: '',
    tags: [],
    featuredArticle: false,
    license: '',
    accessLevel: 'public',
    allowComments: true,
    customFields: {},
    language: 'en',
    sharingOptions: ['twitter', 'facebook'],
    videoEmbeddedCode: '',
    media: [],
    rating: 0,
    customScripts: '',
    customHeader: '',
    customFooter: '',
    articleInteractiveMap: false,
    articleReferences: '',
    articleLocation: '',
    sensitivityWarning: '',
    audioVersion: '',
    pollQuiz: [],
    contributors: [],
    hashtags: [],
    relatedArticles: [],
    expirationDate: null,
    internalNotes: '',
    revisionNotes: '',
    articleVersion: '1.0.0',
    externalLinks: [],
    internalLinks: [],
    customMetaTags: '',
    customUrlRedirect: '',
    archiveDate: null,
    articlePreview: '',
    shortCode: ''
};

const ViewArticle = () => {
    let id = new URLSearchParams(window.location.search).get('id');
    const [article, setArticle] = useState(defaultArticle);
    const [loading, setLoading] = useState(true);
    const [showShareOptions, setShowShareOptions] = useState(false);

    const fetchArticle = async () => {
        try {
            const response = await api.get(`/article/${id}`);
            if (!response.success) {
                toast.error(response?.message || 'Failed to fetch article');
                setTimeout(() => {
                    window.history.back();
                }, 1200);
                return;
            }

            let { mediaUsages, ...articleData } = response?.data;
            if (articleData) {
                const formattedArticle = { ...defaultArticle, ...articleData.seo, ...articleData.settings, ...articleData };
                if (typeof formattedArticle.content === 'string' && formattedArticle.content.startsWith('{')) {
                    formattedArticle.content = JSON.parse(formattedArticle.content);
                }
                if (mediaUsages && mediaUsages?.length) {
                    let media = mediaUsages.map(m => ({
                        id: m.mediaId,
                        type: m.context,
                        url: (process.env.CLOUD_FRONT_URL || 'https://media.pointofindia.in') + m.media.originalUrl,
                        fileName: m?.media?.originalFilename,
                        alt: m?.media?.originalFilename,
                        caption: m?.media?.originalFilename,
                        isNewUpload: false,
                        context: m.context
                    }));
                    formattedArticle['images'] = media;
                }
                setArticle(formattedArticle);
            } else {
                toast.error('Article not found');
            }
        } catch (error) {
            console.error('Error fetching article:', error);
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (article?.customScripts) {
            try {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.innerHTML = article.customScripts;
                document.body.appendChild(script);

                return () => {
                    // cleanup: remove the injected script
                    document.body.removeChild(script);
                };
            } catch (err) {
                console.error("Error executing custom script:", err);
            }
        }
    }, [article?.customScripts]);

    useEffect(() => {
        if (id) {
            fetchArticle();
        } else {
            window.history.back();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleShare = async () => {
        const shareData = {
            title: article?.title ?? 'No title on this Article',
            text: article?.description ?? article?.excerpt ?? 'Check out this article!',
            url: article?.customUrlRedirect ?? window.location.href,
        };

        try {
            if (navigator.share && article?.sharingOptions?.length > 0) {
                await navigator.share(shareData);
            } else {
                setShowShareOptions(!showShareOptions);
            }
        } catch (error) {
            console.error('Error sharing:', error);
            toast.error('Failed to share the article');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const featuredImage = article?.images?.find(img => img.context === 'featured');

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }


    return (
        <div className="container mx-auto p-6">
            <div className="w-full px-3">
                {article?.customHeader && (
                    <div dangerouslySetInnerHTML={{ __html: article?.customHeader }} className="mb-8" />
                )}

                <div className="mb-8">
                    <h1 className="md:text-5xl text-2xl font-semibold">{article?.title ?? 'No title on this Article'}</h1>
                    {article?.subtitle && (
                        <h2 className="text-base text-gray-600 font-medium my-6">{article?.subtitle}</h2>
                    )}
                    <p title="description" className="mt-5 font-light text-sm italic text-[#6c6b6b]">
                        {article?.description}
                    </p>
                </div>

                <div className="flex md:flex-row flex-col gap-3 mt-4 items-center justify-between text-sm py-6">
                    <div className="flex items-start justify-start gap-1 flex-col">
                        <div className="flex items-center gap-2">
                            <div className="flex md:flex-row flex-col items-center md:justify-start justify-center gap-5">
                                <img
                                    loading="lazy"
                                    className="md:w-20 w-24 h-24 md:h-20 rounded-full object-cover"
                                    src={article?.owner?.profilePic || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8dXNlcnxlbnwwfHwwfHx8MA%3D%3D"}
                                    alt={`${article?.owner?.name || article?.author || 'author'}-image`}
                                    onError={(e) => e.target.src = "https://www.aalimec.ac.in/wp-content/uploads/2014/08/default-user.png"}
                                />
                                <div className="md:w-auto w-full">
                                    <h2 className="md:text-xl text-base md:text-start text-center font-medium">
                                        {article?.owner?.name || 'Anonymous'}
                                    </h2>
                                    <div className="flex gap-2 text-sm md:text-start items-center text-center">
                                        <h3 className="text-sm">{article?.owner?.designation ?? 'Journalist'}</h3>
                                        <h3 className="italic text-xs">@{article?.owner?.company ?? 'pointofindia'}</h3>
                                    </div>
                                    <div className="flex items-center md:justify-start justify-center gap-1 text-base mt-1">
                                        {article?.owner?.facebook && (
                                            <a target="_blank" href={`https://facebook.com/${article?.owner?.facebook}`} rel="noopener noreferrer">
                                                <FaFacebookSquare />
                                            </a>
                                        )}
                                        {article?.owner?.instagram && (
                                            <a target="_blank" href={`https://instagram.com/${article?.owner?.instagram}`} rel="noopener noreferrer">
                                                <FaInstagram />
                                            </a>
                                        )}
                                        {article?.owner?.twitter && (
                                            <a target="_blank" href={`https://twitter.com/${article?.owner?.twitter}`} rel="noopener noreferrer">
                                                <FaSquareXTwitter />
                                            </a>
                                        )}
                                        {article?.owner?.linkedin && (
                                            <a target="_blank" href={`https://linkedin.com/in/${article?.owner?.linkedin}`} rel="noopener noreferrer">
                                                <FaLinkedin />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 py-4">
                        <div className="flex items-center gap-4">
                            <div className="flex gap-3 text-gray-500">
                                <button
                                    className="hover:text-blue-500 transition-colors"
                                    onClick={handleShare}
                                    title="Share article"
                                >
                                    <LuShare2 size={20} />
                                </button>
                                <button
                                    className={`transition-colors `}
                                    title={'Bookmark article'}
                                >
                                    <FaBookmark size={20} />
                                </button>
                            </div>
                            {article?.sharingOptions?.length > 0 && (
                                <div className="flex gap-3 text-gray-700">
                                    {article?.sharingOptions?.includes('facebook') && (
                                        <div

                                            className="hover:text-blue-500 transition-colors"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Share on Facebook"
                                        >
                                            <FaFacebookSquare size={20} />
                                        </div>
                                    )}
                                    {article?.sharingOptions?.includes('twitter') && (
                                        <div

                                            className="hover:text-blue-500 transition-colors"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Share on Twitter"
                                        >
                                            <FaSquareXTwitter size={20} />
                                        </div>
                                    )}
                                    {article?.sharingOptions?.includes('whatsapp') && (
                                        <div

                                            className="hover:text-blue-500 transition-colors"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Share on WhatsApp"
                                        >
                                            <FaWhatsapp size={20} />
                                        </div>
                                    )}
                                    {article?.sharingOptions?.includes('linkedin') && (
                                        <div

                                            className="hover:text-blue-500 transition-colors"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Share on LinkedIn"
                                        >
                                            <FaLinkedin size={20} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {article?.expirationDate && (
                    <div className="flex items-center gap-2">
                        <b>Expires:</b>
                        <span className="text-xs">{formatDate(article?.expirationDate)}</span>
                    </div>
                )}

                {article?.sensitivityWarning && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-4 mb-8 rounded-r-lg">
                        <p className="font-bold flex items-center gap-2">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            Content Warning
                        </p>
                        <p className="mt-1">{article.sensitivityWarning}</p>
                    </div>
                )}

                {featuredImage?.url && (
                    <div className="mb-8 rounded-xl overflow-hidden shadow-md">
                        <img
                            loading="lazy"
                            src={featuredImage.url}
                            alt={article.title}
                            className="w-full h-auto max-h-[32rem] object-fill"
                        />
                        {featuredImage.caption && (
                            <p className="text-sm text-gray-500 mt-2 px-4 py-2 bg-gray-50">
                                {featuredImage.caption}
                            </p>
                        )}
                    </div>
                )}

                <article className="mb-8 editor">
                    <Output data={article?.content?.blocks} />
                </article>

                {article?.videoEmbeddedCode && (
                    <div className="mb-8 rounded-xl w-full overflow-hidden">
                        <div dangerouslySetInnerHTML={{ __html: article.videoEmbeddedCode }} />
                    </div>
                )}

                {article?.audioVersion && (
                    <div className="mb-8 p-6 bg-gray-50 rounded-xl shadow-sm">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" />
                            </svg>
                            Audio Version
                        </h3>
                        <audio controls className="w-full">
                            <source src={article.audioVersion} type="audio/mpeg" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                )}
{/* 
                {galleryImages?.length > 0 && (
                    <div className="mb-8">
                        <Gallery
                            label="Related Images"
                            galleryImages={galleryImages}
                            gridColumns="grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                            thumbnailSize="w-full h-48 object-cover rounded-lg"
                        />
                    </div>
                )} */}

                <div className="bg-gray-50 rounded-xl p-6 mb-8">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Article Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {article?.articleSeries && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500">Series</h4>
                                <p className="text-gray-800">{article.articleSeries}</p>
                            </div>
                        )}
                        {article?.publishDate && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500">Published</h4>
                                <span className="text-gray-800">{formatDate(article?.publishDate)}</span>
                            </div>
                        )}
                        {article?.archiveDate && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500">Archived</h4>
                                <span className="text-gray-800">{formatDate(article?.archiveDate)}</span>
                            </div>
                        )}
                        {article?.articleSource && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500">Source</h4>
                                <p className="text-gray-800">{article.articleSource}</p>
                            </div>
                        )}
                        {article?.tags?.length > 0 && (
                            <div className="md:col-span-2">
                                <h4 className="text-sm font-medium text-gray-500">Tags</h4>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {article.tags.map((tag, index) => (
                                        <span key={index} className="inline-block bg-gray-200 rounded-full px-3 py-1 text-sm font-medium text-gray-700">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {article?.hashtags?.length > 0 && (
                            <div className="md:col-span-2">
                                <h4 className="text-sm font-medium text-gray-500">Hashtags</h4>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {article.hashtags.map((hashtag, index) => (
                                        <span key={index} className="inline-block bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm font-medium">
                                            #{hashtag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {article?.contributors?.length > 0 && (
                            <div className="md:col-span-2">
                                <h4 className="text-sm font-medium text-gray-500">Contributors</h4>
                                <ul className="mt-1 space-y-1">
                                    {article.contributors.map((contributor, index) => (
                                        <li key={index} className="text-gray-800">• {contributor}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {article?.articleLocation && (
                            <div className="md:col-span-2">
                                <h4 className="text-sm font-medium text-gray-500">Location</h4>
                                <a href={typeof article.articleLocation === 'string'
                                    ? article.articleLocation
                                    : (article.articleLocation.city
                                        ? `${article.articleLocation.city}${article.articleLocation.state ? ', ' + article.articleLocation.state : ''}${article.articleLocation.country ? ', ' + article.articleLocation.country : ''}`
                                        : 'Unknown location')} className="text-blue-700 mt-1">
                                    Location
                                </a>
                            </div>
                        )}
                        {article?.license && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500">License</h4>
                                <p className="text-gray-800">{article.license}</p>
                            </div>
                        )}
                        {article?.language && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500">Language</h4>
                                <p className="text-gray-800">{article.language}</p>
                            </div>
                        )}
                    </div>
                </div>

                {article?.relatedArticles?.length > 0 && (
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Related Articles</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {article.relatedArticles.map((related, index) => (
                                <a
                                    key={index}
                                    href={related.url || '#'}
                                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all"
                                >
                                    <h4 className="font-medium text-gray-900 mb-1">{related.title || 'Related Article'}</h4>
                                    {related.description && (
                                        <p className="text-sm text-gray-600 line-clamp-2">{related.description}</p>
                                    )}
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {(article?.externalLinks?.length > 0 || article?.internalLinks?.length > 0) && (
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">References</h3>
                        {article?.externalLinks?.length > 0 && (
                            <div className="mb-4">
                                <h4 className="text-md font-medium mb-2 text-gray-700">External Links</h4>
                                <ul className="space-y-2">
                                    {(article.externalLinks[0])?.split(' ').map((link, index) => {
                                        const linkUrl = typeof link === 'string' ? link : link.url;
                                        const linkTitle = typeof link === 'string' ? link : (link.title || link.url);
                                        return (
                                            <li key={index} className="flex items-start">
                                                <span className="text-gray-500 mr-2 mt-1">•</span>
                                                <a
                                                    href={linkUrl || '#'}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                                                >
                                                    {linkTitle}
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                        {article?.internalLinks?.length > 0 && (
                            <div>
                                <h4 className="text-md font-medium mb-2 text-gray-700">Internal Links</h4>
                                <ul className="space-y-2">
                                    {(article.internalLinks[0])?.split(' ').map((link, index) => {
                                        const linkUrl = typeof link === 'string' ? link : link.url;
                                        const linkTitle = typeof link === 'string' ? link : (link.title || link.url);
                                        return (
                                            <li key={index} className="flex items-start">
                                                <span className="text-gray-500 mr-2 mt-1">•</span>
                                                <a
                                                    href={linkUrl || '#'}
                                                    className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                                                >
                                                    {linkTitle}
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 text-sm">
                    {article?.articleVersion && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <h4 className="font-medium text-gray-500">Version</h4>
                            <p className="text-gray-800">{article.articleVersion}</p>
                        </div>
                    )}
                    {article?.rating > 0 && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <h4 className="font-medium text-gray-500">Rating</h4>
                            <div className="flex items-center mt-1">
                                {[...Array(5)].map((_, i) => (
                                    <svg
                                        key={i}
                                        className={`w-4 h-4 ${i < article.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                ))}
                                <span className="ml-1 text-gray-600">({article.rating}/5)</span>
                            </div>
                        </div>
                    )}
                    {article?.shortCode && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <h4 className="font-medium text-gray-500">Short Code</h4>
                            <p className="text-gray-800 font-mono">{article.shortCode}</p>
                        </div>
                    )}
                    {article?.category && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <h4 className="font-medium text-gray-500">Category</h4>
                            <p className="text-gray-800 capitalize">{article.category}</p>
                        </div>
                    )}
                    {article?.accessLevel && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <h4 className="font-medium text-gray-500">Access Level</h4>
                            <p className="text-gray-800 capitalize">{article.accessLevel}</p>
                        </div>
                    )}
                </div>

                {article?.customFooter && (
                    <div
                        dangerouslySetInnerHTML={{ __html: article.customFooter }}
                        className="mt-8 border-t pt-8"
                    />
                )}
            </div>

            <div className="mt-6 flex justify-end space-x-4">
                <button
                    onClick={() => window.history.back()}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                    Back
                </button>
                <a
                    href={`/content/article?id=${id}`}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Edit Article
                </a>
            </div>
        </div>
    );
};

export default ViewArticle;